/**
 * Общий хук для пагинированных запросов.
 * Оборачивает TanStack Query с управлением page/limit/sort/filters,
 * расчётом totalPages и keepPreviousData для плавных переходов.
 *
 * Два режима. usePaginatedQuery владеет страницей, лимитом и порядком сам и отдаёт их сеттеры.
 * useServerWindow — окно, которым владеет вызывающий (подробные списки хранилищ: страница и
 * порядок в адресе): своих страницы и порядка у него нет, сеттеров тоже — иначе они молча ничего
 * бы не делали, а порядок оказался бы у двух владельцев.
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';

/** Дефолтный лимит записей на странице */
const DEFAULT_LIMIT = 20;

/** Окно дочерних списков хранилищ (схемы, семплы, коллекции): столько же, сколько сервер отдаёт без параметров */
export const CHILD_LIST_LIMIT = 50;

/** Параметры, передаваемые в fetchFn */
export interface PaginatedFetchParams {
	limit: number;
	offset: number;
	sort?: string;
	[key: string]: string | number | boolean | undefined;
}

/** Ответ от бэкенда — содержит items + total */
export interface PaginatedResult<T> {
	items: T[];
	total: number;
	totalFiltered?: number;
	/** Всего записей без фильтров — «M» счётчика «N из M» */
	overallTotal?: number;
}

/** Настройки хука */
interface UsePaginatedQueryOptions<T> {
	/** Ключ кеша TanStack Query */
	queryKey: QueryKey;
	/** Функция запроса — принимает params, возвращает { items, total } */
	queryFn: (params: PaginatedFetchParams) => Promise<PaginatedResult<T>>;
	/** Записей на странице (default 20) */
	limit?: number;
	/** Начальная сортировка (формат "field:asc") */
	initialSort?: string;
	/** Фильтры — объект, меняющийся при изменении фильтров */
	filters?: Record<string, string | number | boolean | undefined>;
	/** Включён ли запрос (default true) */
	enabled?: boolean;
}

/** Окно, которым владеет вызывающий: страница, лимит и порядок — его параметры */
interface ServerWindowOptions<T> {
	queryKey: QueryKey;
	queryFn: (params: PaginatedFetchParams) => Promise<PaginatedResult<T>>;
	limit: number;
	/** Страница, начиная с 1 */
	page: number;
	/** Порядок в формате сервера; без него — порядок сервера по умолчанию */
	sort?: string;
	filters?: Record<string, string | number | boolean | undefined>;
	enabled?: boolean;
}

/** Окно списка сервера: строки и числа ответа и состояние запроса, без сеттеров */
export function useServerWindow<T>({
	queryKey,
	queryFn,
	limit,
	page,
	sort,
	filters = {},
	enabled = true,
}: ServerWindowOptions<T>) {
	const filtersKey = JSON.stringify(filters);

	// Полный ключ кеша с учётом пагинации, сортировки и фильтров
	const fullQueryKey = useMemo(
		() => [...queryKey, { page, limit, sort, filters: filtersKey }],
		[queryKey, page, limit, sort, filtersKey],
	);

	// Параметры для fetchFn
	const fetchParams = useMemo<PaginatedFetchParams>(
		() => ({
			limit,
			offset: (page - 1) * limit,
			sort,
			...filters,
		}),
		[page, limit, sort, filters],
	);

	const query = useQuery({
		queryKey: fullQueryKey,
		queryFn: () => queryFn(fetchParams),
		placeholderData: keepPreviousData,
		enabled,
	});

	const total = query.data?.total ?? 0;
	const totalFiltered = query.data?.totalFiltered;
	// Страницы — по найденным с фильтрами: total сервера считает все записи без фильтров
	const totalPages = Math.max(1, Math.ceil((totalFiltered ?? total) / limit));

	return {
		// Данные
		data: query.data,
		items: query.data?.items ?? [],
		total,
		totalFiltered,
		overallTotal: query.data?.overallTotal,
		totalPages,
		page,
		limit,
		sort,

		// TanStack Query состояние
		isLoading: query.isLoading,
		isFetching: query.isFetching,
		isError: query.isError,
		isSuccess: query.isSuccess,
		error: query.error,
		isPending: query.isPending,
		isPlaceholderData: query.isPlaceholderData,
		/** Повтор после сбоя: «Повторить» состояния ошибки списка */
		refetch: query.refetch,
	};
}

export type ServerWindow<T> = ReturnType<typeof useServerWindow<T>>;

export function usePaginatedQuery<T>({
	queryKey,
	queryFn,
	limit: initialLimit = DEFAULT_LIMIT,
	initialSort,
	filters = {},
	enabled = true,
}: UsePaginatedQueryOptions<T>) {
	const [limit, setLimit] = useState(initialLimit);
	const [sort, setSort] = useState(initialSort);

	/*
	 * Страница принадлежит фильтрам, при которых её выбрали: другие фильтры — первая страница.
	 * Иначе со второй страницы поиск, который находит одну страницу, запрашивал бы offset за
	 * пределами результата и показывал пустой список без пагинации.
	 */
	const filtersKey = JSON.stringify(filters);
	const [pageState, setPageState] = useState({ page: 1, filtersKey });
	const page = pageState.filtersKey === filtersKey ? pageState.page : 1;
	const setPage = useCallback(
		(next: number) => setPageState({ page: next, filtersKey }),
		[filtersKey],
	);

	const serverWindow = useServerWindow({ queryKey, queryFn, limit, page, sort, filters, enabled });
	const { totalPages } = serverWindow;

	// Навигация
	const goToPage = useCallback(
		(p: number) => {
			setPage(Math.max(1, Math.min(p, totalPages)));
		},
		[setPage, totalPages],
	);

	const nextPage = useCallback(() => goToPage(page + 1), [goToPage, page]);
	const prevPage = useCallback(() => goToPage(page - 1), [goToPage, page]);

	// Изменение сортировки сбрасывает на первую страницу
	const changeSort = useCallback(
		(newSort: string) => {
			setSort(newSort);
			setPage(1);
		},
		[setPage],
	);

	// Изменение лимита сбрасывает на первую страницу
	const changeLimit = useCallback(
		(newLimit: number) => {
			setLimit(newLimit);
			setPage(1);
		},
		[setPage],
	);

	// Сброс на первую страницу (для фильтров)
	const resetPage = useCallback(() => setPage(1), [setPage]);

	return {
		// Данные
		data: serverWindow.data,
		items: serverWindow.items,
		total: serverWindow.total,
		totalFiltered: serverWindow.totalFiltered,
		overallTotal: serverWindow.overallTotal,
		totalPages,

		// Пагинация
		page,
		limit,
		goToPage,
		nextPage,
		prevPage,
		hasNextPage: page < totalPages,
		hasPrevPage: page > 1,

		// Сортировка
		sort,
		changeSort,

		// Лимит
		changeLimit,

		// Сброс
		resetPage,

		// TanStack Query состояние
		isLoading: serverWindow.isLoading,
		isFetching: serverWindow.isFetching,
		isError: serverWindow.isError,
		isSuccess: serverWindow.isSuccess,
		error: serverWindow.error,
		isPending: serverWindow.isPending,
		isPlaceholderData: serverWindow.isPlaceholderData,
		/** Повтор после сбоя: «Повторить» состояния ошибки списка */
		refetch: serverWindow.refetch,
	};
}
